import {
  EDU_EDITION_ORDER,
  EDU_EDITIONS,
  type EduEditionId,
} from "@itaas/edu-editions";
import type { ProductPromotion } from "@itaas/pep-contracts";
import { ProductPromotionBadge } from "../../components/commercial/ProductPromotionBadge";
import { PromoPriceUsd } from "../../components/commercial/PromoPrice";
import {
  discountedAmount,
  isPromotionActive,
  resolveActivePromotion,
} from "../../utils/product-promotion";

interface TrialEduEditionStepProps {
  value: EduEditionId;
  onChange: (edition: EduEditionId) => void;
  promotion?: ProductPromotion | null;
}

export function TrialEduEditionStep({ value, onChange, promotion }: TrialEduEditionStepProps) {
  return (
    <div className="mb-4">
      <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
        <h2 className="h6 fw-bold mb-0">Choisissez l&apos;édition qui correspond à votre établissement</h2>
        {isPromotionActive(promotion) ? (
          <ProductPromotionBadge promotion={promotion} locale="fr" />
        ) : null}
      </div>
      <p className="text-secondary small mb-3">
        Chaque édition inclut les modules de la précédente. Vous pourrez découvrir les fonctionnalités
        des éditions supérieures en mode aperçu dans l&apos;application.
      </p>
      <div className="row g-3">
        {EDU_EDITION_ORDER.map((id) => {
          const ed = EDU_EDITIONS[id];
          const activePromo = resolveActivePromotion(promotion, id);
          const priceContent =
            ed.monthlyUsd === null ? (
              <span className="pep-edition-card__price">Sur devis</span>
            ) : activePromo ? (
              <span className="pep-edition-card__price">
                <span className="d-block small text-secondary mb-1">À partir de</span>
                <PromoPriceUsd
                  originalUsd={ed.monthlyUsd}
                  promoUsd={discountedAmount(ed.monthlyUsd, activePromo.discountPercent)}
                />
                <span className="d-block small text-secondary mt-1">USD/mois</span>
              </span>
            ) : (
              <span className="pep-edition-card__price">À partir de {ed.monthlyUsd} USD/mois</span>
            );
          return (
            <div className="col-md-6 col-xl-3" key={id}>
              <button
                type="button"
                className={`pep-edition-card w-100 text-start h-100${value === id ? " is-selected" : ""}${ed.highlight ? " is-recommended" : ""}`}
                onClick={() => onChange(id)}
              >
                {ed.highlight ? <span className="pep-edition-card__badge">Populaire</span> : null}
                {activePromo ? (
                  <span className="pep-edition-card__badge pep-edition-card__badge--promo">
                    {activePromo.badgeFr ?? `−${activePromo.discountPercent} %`}
                  </span>
                ) : null}
                <span className="pep-edition-card__name">{ed.displayName.replace("ITaaS Edu ", "")}</span>
                <span className="pep-edition-card__tagline">{ed.tagline}</span>
                {priceContent}
                <span className="pep-edition-card__capacity">{ed.maxStudentsLabel}</span>
                <ul className="pep-edition-card__modules">
                  {(ed.newInEdition ?? ed.modules).slice(0, 5).map((m) => (
                    <li key={m}>{m}</li>
                  ))}
                  {(ed.newInEdition ?? ed.modules).length > 5 ? (
                    <li className="text-secondary">+ autres modules</li>
                  ) : null}
                </ul>
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
